import { theme } from '../theme';
import { CATEGORY_LABELS, CATEGORY_KEYS, WIM_HANDLING_IDS, getCategoriesForReasonCode, expandCategoryRange } from '../constants';
import { ownsMsbKVocabulary, type KnownRole } from './roleRegistry';
import type { ClassificationSchema, ClassificationVocabulary } from './api';

export const DEFAULT_ABBR: Record<string, string> = {
  versorgerwechsel: 'VW', netzabrechnung: 'NA', messwerte: 'MW',
  bilanzierung: 'BIL', stammdaten: 'STD', edifact_transport: 'EDI',
  geraetewechsel: 'GW', marktpartner_vertrag: 'MPV', sonstiges: 'SON',
  // einspeisung (served since schema content v5) had no entry and fell to the served-label tier
  // ("Einspeisung" on a strip built for 2–3 letters); unbestimmt (schema v9, the vnb unclassified value —
  // "Nicht bestimmbar") gets the initialism the way the K/H codes get their identity entry.
  einspeisung: 'EIN', unbestimmt: 'NB',
  // H-codes: the code IS the abbreviation — a recorded decision, not the slice() accident.
  ...Object.fromEntries(WIM_HANDLING_IDS.map(h => [h, h])),
  // K-codes, for exactly the same reason and finally BY RECORD. roleRegistry's own membership rule
  // has described these as "carrying their own DEFAULT_ABBR identity entry" since before it was
  // true — they reached the same strings through slice(0, 3), a coincidence. It stopped being
  // harmless once a served MSB vocabulary existed: without an entry a K-code falls past this floor
  // into the served-LABEL tier below and renders its full German word where the strip shows a code.
  ...Object.fromEntries(CATEGORY_KEYS.map(k => [k, k])),
};

const mp = (s: ClassificationSchema | null, id: string) => s?.marktprozess.find(v => v.id === id);
const an = (s: ClassificationSchema | null, id: string) => s?.anliegen.find(v => v.id === id);

// THE placeholder floor. Tier 1-2 (schema/vocabulary truth, tested from the call sites above this
// function) are unchanged and unconditional. Tier 3 — the hardcoded CATEGORY_LABELS K/H-word map —
// is gMSB/wMSB's OWN rendered truth, never a generic backstop: a role that carries neither a
// schema nor a vocabulary is not "gMSB-shaped by default", it is a role this seam has not been
// told how to label, and showing it gMSB's German words would be exactly the silent-gmsb-assumption
// this arc's Task 3 already killed at the identity layer. Every other role — including an
// unresolved (null) or a drifted/future (not yet in roleRegistry) role — gets the raw, honest
// placeholder instead of a borrowed word.
export function resolveLabel(schema: ClassificationSchema | null, value: string, role: KnownRole | string | null | undefined): string {
  const fromSchema = schema ? (mp(schema, value)?.label ?? an(schema, value)?.label) : undefined;
  if (fromSchema) return fromSchema;
  if (ownsMsbKVocabulary(role)) return CATEGORY_LABELS[value] ?? value;
  return `{category.${value}}`;
}

// The mapping counterpart of resolveLabel — same seam, same tier order (D10 addendum 4): a served
// grund_code row's OWN minCategory/maxCategory first, the baked REASON_CODES pair as the floor.
// Takes `vocab` directly (unlike resolveLabel, which is handed a pre-resolved label map by its
// caller) because the served attributes this reads (minCategory/maxCategory) live only on the
// secondary axis's raw SchemaValue rows, not on any derived lookup context.tsx already builds.
//
// Tier semantics are per-ID, identical to resolveLabel's `vocabLabels?.get(v) ?? resolveLabel(...)`
// — never per-attribute. D10 addendum 5 (BINDING): labels-served-mapping-baked is banned in ANY
// form. Once a served row exists for `code`, BOTH its label and its mapping come from that row —
// a served row with no min/max attributes is a REAL state (per-role constraint kinds are
// optional-by-shape; the baked floor itself carries this exact state for `sonstiges`/`unbestimmt`),
// and the honest answer is "no derivable range" ([]), never a silent reach into the baked floor's
// range for the same id. Only when the vocabulary serves NO row for `code` at all does the floor
// apply — exactly the state every MSB tenant is in before its vocabulary rows are delivered.
export function resolveCategoriesForSecondaryAxisCode(vocab: ClassificationVocabulary | null | undefined, code: string): string[] {
  const served = vocab?.secondary?.values.find(v => v.id === code);
  if (served) {
    if (!served.minCategory || !served.maxCategory) return [];
    return expandCategoryRange(served.minCategory, served.maxCategory);
  }
  return getCategoriesForReasonCode(code);
}

export function resolveColor(schema: ClassificationSchema | null, value: string): string {
  if (schema && mp(schema, value)) return theme.colors.marktprozess[value as keyof typeof theme.colors.marktprozess] ?? theme.colors.navyMid;
  // wim handling ids (H1–H4) resolve schema-independently: the wim schema's axes carry no
  // per-value palette, and the handling identity color must hold on every surface that
  // renders a category (list border, chips, stats) — same lookup shape as the K-map below.
  if (value in theme.colors.wimHandling) return theme.colors.wimHandling[value as keyof typeof theme.colors.wimHandling];
  // schema-active anliegen ids have no color palette entry; intentional fall-through to navyMid
  return theme.colors.categories[value as keyof typeof theme.colors.categories] ?? theme.colors.navyMid;
}

/** The value's COMPACT SPELLING, in tier order — the abbreviation counterpart of resolveLabel, and
 *  it takes `vocab` directly for the same reason resolveCategoriesForSecondaryAxisCode does: the
 *  attribute it reads lives only on the raw SchemaValue rows.
 *
 *  1. THE VALUE'S OWN `abbr`. A tenant that declares its compact form is not overruled by a baked
 *     map it has never heard of — that is what makes re-wording an abbreviation a data edit.
 *  2. DEFAULT_ABBR. The identity codes (H1–H4, K0–KU) and the VNB marktprozess set: recorded
 *     decisions about ids the product owns, which is why they outrank a served LABEL — for a value
 *     whose id already IS its abbreviation, its label is the long German word, not a shorter one.
 *  3. THE SERVED LABEL. A served value that declares no abbreviation has, at worst, the tenant's own
 *     word for itself; showing that is honest. What it must NOT do is fall to the truncation below —
 *     `lastgang_bilanzierung` → "LAS" is a spelling nobody chose, and consistent with
 *     resolveCategoriesForSecondaryAxisCode refusing with [] rather than reaching into a baked
 *     range, a served row answers from itself or not at all.
 *  4. THE TRUNCATION, for values the vocabulary does not serve AT ALL — and only those. ADJUDICATED
 *     rather than survived: with no served row there is no label to floor to, and the alternatives
 *     are the raw id (longer than the strip it renders in) or a placeholder inside a fixed-width
 *     badge. This is every legacy path — a VNB tenant on a schema, an MSB tenant before delivery. */
export function resolveAbbr(vocab: ClassificationVocabulary | null | undefined, value: string): string {
  const served = vocab
    ? vocab.primary.values.find(v => v.id === value) ?? vocab.secondary?.values.find(v => v.id === value)
    : undefined;
  if (served?.abbr) return served.abbr;
  if (DEFAULT_ABBR[value]) return DEFAULT_ABBR[value];
  if (served) return served.label;
  return value.slice(0, 3).toUpperCase();
}

/** Pure helper: returns the UI noun for the classification axis.
 *  VNB (schema present) → 'Marktprozess'; gMSB/wMSB (no schema, legacy K-vocabulary role) →
 *  'Kategorie'; every other/unresolved role → the raw placeholder — same floor as resolveLabel,
 *  same reason: 'Kategorie' is gMSB/wMSB's own word, not a generic no-schema default. */
export function resolveCategoryNoun(schema: ClassificationSchema | null, role: KnownRole | string | null | undefined): string {
  if (schema) return 'Marktprozess';
  return ownsMsbKVocabulary(role) ? 'Kategorie' : '{category.noun}';
}

/** Pure helper: UI noun for the SECOND classification axis.
 *  VNB (schema present) → 'Anliegen'; gMSB/wMSB (no schema) → 'Grund-Code'; every other/unresolved
 *  role → the raw placeholder. */
export function resolveSecondaryAxisNoun(schema: ClassificationSchema | null, role: KnownRole | string | null | undefined): string {
  if (schema) return 'Anliegen';
  return ownsMsbKVocabulary(role) ? 'Grund-Code' : '{anliegen.noun}';
}

/** Pure helper: the PLURAL of the second-axis noun, for the sites that count things
 *  ("Häufigste …", "Alle …", "Weitere …").
 *  VNB → 'Anliegen' (invariant plural); gMSB/wMSB → 'Grund-Codes' (takes -s); every other/
 *  unresolved role → the raw placeholder.
 *  It lives beside resolveSecondaryAxisNoun rather than being derived from it because German
 *  plurals are not a suffix rule — deriving one would be right here and wrong at the next noun. */
export function resolveSecondaryAxisNounPlural(schema: ClassificationSchema | null, role: KnownRole | string | null | undefined): string {
  if (schema) return 'Anliegen';
  return ownsMsbKVocabulary(role) ? 'Grund-Codes' : '{anliegen.nounPlural}';
}

/** Tint a marktprozess hex into {fill,border,text} for gap chips / draft inputs.
 *  Fill = base blended ~90% toward white (a light tint); border/text = the base hue. */
export function marktprozessTint(hex: string): { fill: string; border: string; text: string } {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * 0.9);
  const fill = `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
  return { fill, border: hex, text: hex };
}
